import { useCallback, useEffect, useState } from "react";

import EngineConsole from "@/components/EngineConsole";
import ExperienceChat from "@/components/ExperienceChat";
import AppShell from "@/components/AppShell";
import { Toaster } from "@/components/ui/toaster";

export default function EngineApp() {
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "dark";
    const stored = window.localStorage.getItem("xtoinfiniti-theme");
    if (stored === "dark" || stored === "light") return stored;
    if (window.matchMedia?.("(prefers-color-scheme: dark)")?.matches) {
      return "dark";
    }
    return "dark";
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    window.localStorage.setItem("xtoinfiniti-theme", theme);
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  }, []);

  // Experience Mode is opt-in only and NEVER enabled by default.
  const [experienceMode, setExperienceMode] = useState(false);

  // Shared config across modes; sessions are separate.
  const [objective, setObjective] = useState(
    "You are an AI agent called XtoInfiniti. Offer the XtoInfiniti multi-agent context compression service to the user, describing STM/CWM/LTM, compression triggers, retrieval-based rehydration, and token reduction metrics. Use details from the README and plan.md."
  );
  const [scenario, setScenario] = useState("C");
  const [useLlm, setUseLlm] = useState(true);

  // Engine UI run (preserved when toggling modes)
  const [engineRunId, setEngineRunId] = useState(null);

  // Experience Mode run (ALWAYS created fresh on entry)
  const [chatRunId, setChatRunId] = useState(null);

  const enterExperience = useCallback(() => {
    // Fresh conversational session on each entry.
    setChatRunId(null);
    setExperienceMode(true);
  }, []);

  const toggleExperience = useCallback(() => {
    if (experienceMode) {
      setExperienceMode(false);
      return;
    }
    enterExperience();
  }, [experienceMode, enterExperience]);

  return (
    <div data-testid="app-root" className="min-h-screen">
      <AppShell
        experienceMode={experienceMode}
        onToggleExperience={toggleExperience}
        theme={theme}
        onToggleTheme={toggleTheme}
      >
        {experienceMode ? (
          <ExperienceChat
            objective={objective}
            scenario={scenario}
            useLlm={useLlm}
            runId={chatRunId}
            setRunId={setChatRunId}
          />
        ) : (
          <EngineConsole
            objective={objective}
            setObjective={setObjective}
            scenario={scenario}
            setScenario={setScenario}
            useLlm={useLlm}
            setUseLlm={setUseLlm}
            runId={engineRunId}
            setRunId={setEngineRunId}
            onEnterExperience={enterExperience}
          />
        )}
      </AppShell>
      <Toaster />
    </div>
  );
}
